
import { useI18n } from 'vue-i18n';
import {
  defineComponent, onMounted, ref, computed,
} from 'vue';
import { useConfirm } from 'primevue/useconfirm';
import Dialog from 'primevue/dialog';
import InputText from 'primevue/inputtext';
import Tag from 'primevue/tag';
import ProgressSpinner from 'primevue/progressspinner';
import ConfirmDialog from 'primevue/confirmdialog';
import SpmOverlayPanel from '@/components/spm-primevue/SpmOverlayPanel.vue';
import SpmButton from '@/components/spm-primevue/SpmButton.vue';
import { showToastError, showToastSuccess } from '@/helpers';
import {
  required, minLength, maxLength, helpers,
} from '@vuelidate/validators';
import useVuelidate from '@vuelidate/core';
import { nestCall, NestApiType } from '@/composables/nestApi';
import { UserState } from '@/composables/User';

interface ApiKey {
  id: number;
  key_name: string;
  prefix: string;
  scopes: string[];
  rate_limit_per_min: number | null;
  created_at: string;
  last_used_at: string | null;
}

interface CreateApiKeyRequest {
  key_name: string;
}

interface CreateApiKeyResponse {
  id: number;
  full_key: string;
  key_name: string;
  prefix: string;
  scopes: string[];
  rate_limit_per_min: number | null;
  created_at: string;
}

export default defineComponent({
  name: 'ShopApiAccess',
  components: {
    SpmButton,
    Dialog,
    InputText,
    Tag,
    ProgressSpinner,
    ConfirmDialog,
    SpmOverlayPanel,
  },

  setup() {
    const { t } = useI18n();
    const confirm = useConfirm();

    // State
    const createPanel = ref();
    const closeCreatePanel = ref(false);
    const apiKeys = ref<ApiKey[]>([]);
    const shopIdentifier = ref<string | null>(null);
    const loading = ref(false);
    const showSuccessModal = ref(false);
    const createdKey = ref<CreateApiKeyResponse | null>(null);
    const keyCopied = ref(false);

    // Form
    const createForm = ref<CreateApiKeyRequest>({
      key_name: '',
    });

    const shopId = computed(() => UserState.activeShop?.id);

    // API Methods
    const loadApiKeys = async () => {
      if (!shopId.value) return;
      loading.value = true;
      try {
        const response = await nestCall('get', NestApiType.DEFAULT, `/shop/keys/${shopId.value}`);
        if (response?.success) {
          apiKeys.value = response.data.keys;
          shopIdentifier.value = response.data.identifiant;
        } else {
          throw new Error(response?.error?.message || t('apiKeys.errors.loadFailed'));
        }
      } catch (err: any) {
        await showToastError(err.message);
      } finally {
        loading.value = false;
      }
    };

    const revokeKey = async (key: ApiKey) => {
      if (!shopId.value) return;
      loading.value = true;
      try {
        const response = await nestCall('delete', NestApiType.DEFAULT, `/shop/keys/${shopId.value}/${key.id}`);
        if (response?.success) {
          apiKeys.value = apiKeys.value.filter((k) => k.id !== key.id);
          await showToastSuccess(t('apiKeys.toasts.revoked'));
        } else {
          throw new Error(response?.error?.message || t('apiKeys.errors.revokeFailed'));
        }
      } catch (err: any) {
        await showToastError(err.message);
      } finally {
        loading.value = false;
      }
    };

    const copyIdentifier = async () => {
      if (!shopIdentifier.value) return;
      try {
        await navigator.clipboard.writeText(shopIdentifier.value);
        await showToastSuccess(t('apiKeys.toasts.copied'));
      } catch (err) {
        await showToastError('Failed to copy');
      }
    };

    const copyKey = async () => {
      if (!createdKey.value) return;
      try {
        await navigator.clipboard.writeText(createdKey.value.full_key);
        keyCopied.value = true;
        await showToastSuccess(t('apiKeys.toasts.copied'));
      } catch (err) {
        await showToastError('Failed to copy');
      }
    };

    // Custom validator for alphanumeric and spaces
    const alphaNumSpaces = helpers.withMessage(
      () => t('apiKeys.validation.alphaNumSpaces'),
      (value: string) => !value || /^[a-zA-Z0-9\s]+$/.test(value),
    );

    const rules = {
      key_name: {
        required: helpers.withMessage(() => t('vuelidate.required'), required),
        minLength: helpers.withMessage(() => t('vuelidate.minLength', { min: 3 }), minLength(3)),
        maxLength: helpers.withMessage(() => t('vuelidate.maxLength', { max: 20 }), maxLength(20)),
        alphaNumSpaces,
      },
    };

    const v$ = useVuelidate(rules, createForm);

    const toggleCreatePanel = (event: Event) => {
      createPanel.value.toggle(event);
      closeCreatePanel.value = false;
      // Trigger validation to show errors immediately
      v$.value.$touch();
    };

    const resetCreateForm = () => {
      createForm.value = { key_name: '' };
    };

    const handleCreateKey = async () => {
      if (!shopId.value) return;

      const success = await v$.value.$validate();

      if (!success) {
        return;
      }

      loading.value = true;
      try {
        const payload: CreateApiKeyRequest = {
          key_name: createForm.value.key_name,
        };
        const response = await nestCall('post', NestApiType.DEFAULT, `/shop/keys/${shopId.value}`, {}, '', payload);
        if (response?.success) {
          closeCreatePanel.value = true;
          createdKey.value = response.data;
          showSuccessModal.value = true;
          keyCopied.value = false;
          resetCreateForm();
          await loadApiKeys();
          await showToastSuccess(t('apiKeys.toasts.created'));
        } else {
          throw new Error(response?.error || t('apiKeys.errors.createFailed'));
        }
      } catch (err: any) {
        const errorMessage = err.message === 'api_keys_limit_reached'
          ? t('apiKeys.errors.limitReached')
          : t('apiKeys.errors.createFailed');
        await showToastError(errorMessage);
      } finally {
        loading.value = false;
      }
    };

    const confirmRevoke = (key: ApiKey) => {
      confirm.require({
        message: t('apiKeys.confirmRevoke', { prefix: key.prefix, name: key.key_name }),
        header: t('common.confirmation'),
        icon: 'fas fa-exclamation-triangle',
        accept: () => {
          revokeKey(key);
        },
      });
    };

    onMounted(loadApiKeys);

    return {
      t,
      apiKeys,
      shopIdentifier,
      loading,
      showSuccessModal,
      createdKey,
      keyCopied,
      createForm,
      createPanel,
      closeCreatePanel,
      loadApiKeys,
      handleCreateKey,
      confirmRevoke,
      copyKey,
      copyIdentifier,
      toggleCreatePanel,
      v$,
    };
  },
});
